import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  SheetActions,
  SheetHeader,
  SheetTitle,
  usePreventSheetClose,
} from "@/components/ui/sheet";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Department,
  useUpdateEmployeesToDepartmentMutation,
} from "@common/utils/graphql";
import { zodResolver } from "@hookform/resolvers/zod";
import { SheetBackButton } from "@common/components/SheetActions/SheetBackButton";
import { MultiEmployeeSelect } from "@common/components/EmployeeSelect/MultiEmployeeSelect";
import { useToast } from "@/components/ui/use-toast";
import { hasDirtyFields } from "@/utils/utils";

interface Props {
  onBack: () => void;
  department: Department;
}

const EmailSchema = z.object({
  userIds: z.array(z.string()),
});

export const InviteExistingUserContent = ({ onBack, department }: Props) => {
  const { toast } = useToast();
  const [updateEmployeesToDept, { loading: updateEmployeesToDeptLoading }] =
    useUpdateEmployeesToDepartmentMutation();

  const addUser = async () => {
    const output = await form.trigger(undefined, { shouldFocus: true });
    if (!output) {
      return;
    }

    const values = form.getValues();
    try {
      const { data } = await updateEmployeesToDept({
        variables: {
          departmentId: Number(department.id),
          // TODO: this should be using a different component / endpint
          userIds: values.userIds,
        },
      });

      if (
        data?.updateEmployeesToDepartment.__typename !==
        "MutationUpdateEmployeesToDepartmentSuccess"
      ) {
        throw new Error();
      }
      onBack();
    } catch (e) {
      toast({
        title: `Failed to add ${values.userIds.length === 1 ? "user" : "users"} to ${department.name}, please try again`,
        variant: "destructive",
      });
    }
  };

  const form = useForm<z.infer<typeof EmailSchema>>({
    resolver: zodResolver(EmailSchema),
  });
  usePreventSheetClose(() => hasDirtyFields(form));

  return (
    <>
      <SheetBackButton onClick={onBack} />
      <SheetHeader>
        <SheetTitle>Add User to {department.name}</SheetTitle>
      </SheetHeader>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(() => {})}
          className="flex flex-1 flex-col space-y-4 px-sheet-gap pb-sheet-gap"
        >
          <FormField
            control={form.control}
            disabled={updateEmployeesToDeptLoading}
            name="userIds"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex justify-between">User</FormLabel>
                <FormControl>
                  <MultiEmployeeSelect
                    placeholder="Select Users"
                    onChange={(newList) =>
                      field.onChange(newList.map((list) => list.userId))
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </form>
      </Form>
      <SheetActions>
        <Button
          className="w-full"
          onClick={() => addUser()}
          disabled={updateEmployeesToDeptLoading}
        >
          {updateEmployeesToDeptLoading ? "Adding User..." : "Add User"}
        </Button>
      </SheetActions>
    </>
  );
};
